import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

/**
 * 종목 태그 뱃지 — Phase 7
 *
 * 카테고리별 색상 (시드 042 의 color_class 정책과 동기화):
 *   style       → blue
 *   size        → gray
 *   sector      → purple
 *   theme       → orange
 *   trait       → green
 *   risk        → red
 *   valuechain  → indigo
 *
 * 클릭 시 /tag/:slug 이동 (asLink=true 기본). asLink=false 면 단순 표시용.
 */
export type TagCategory =
  | 'style' | 'size' | 'sector' | 'theme' | 'trait' | 'risk' | 'valuechain';

export interface TagBadgeProps {
  slug: string;
  name: string;
  category: TagCategory | string;
  size?: 'sm' | 'md';
  asLink?: boolean;
  /** 'ai' 등 source 표시용 점 (옵션). 표시 안 하려면 undefined */
  source?: 'rule' | 'ai' | 'manual';
  className?: string;
}

const CATEGORY_CLASSES: Record<string, string> = {
  style:      'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200',
  size:       'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-200',
  sector:     'bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-200',
  theme:      'bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-200',
  trait:      'bg-green-100 text-green-700 hover:bg-green-200 border-green-200',
  risk:       'bg-red-100 text-red-700 hover:bg-red-200 border-red-200',
  valuechain: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200 border-indigo-200',
};

const SIZE_CLASSES: Record<NonNullable<TagBadgeProps['size']>, string> = {
  sm: 'text-[10px] px-1.5 py-0.5',
  md: 'text-xs px-2 py-0.5',
};

export function TagBadge({
  slug,
  name,
  category,
  size = 'md',
  asLink = true,
  source,
  className,
}: TagBadgeProps) {
  const colorCls = CATEGORY_CLASSES[category] ?? CATEGORY_CLASSES.style;
  const cls = cn(
    'inline-flex items-center gap-1 rounded-full border font-medium transition-colors whitespace-nowrap',
    SIZE_CLASSES[size],
    colorCls,
    className,
  );

  const content = (
    <>
      {source === 'ai' && (
        <span
          className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-60"
          title="AI 분석 결과"
          aria-label="AI"
        />
      )}
      {name}
    </>
  );

  if (!asLink) return <span className={cls}>{content}</span>;
  return (
    <Link to={`/tag/${slug}`} className={cls}>
      {content}
    </Link>
  );
}
